import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, ArrowRight } from 'lucide-react';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PageBanner from '@/components/shared/PageBanner';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import JsonLd, { generateBlogPostSchema, generateBreadcrumbSchema } from '@/components/seo/JsonLd';
import { publicApi } from '@/lib/api';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [postRes, recentRes, categoriesRes] = await Promise.all([
          publicApi.getBlogPost(slug),
          publicApi.getRecentPosts(5),
          publicApi.getCategories()
        ]);
        setPost(postRes.data);
        setRecentPosts(recentRes.data.filter(p => p.slug !== slug));
        setCategories(categoriesRes.data);
      } catch (error) {
        setError('Post not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Update document title and meta tags when post loads
  useEffect(() => {
    if (post) {
      // Set document title
      document.title = `${post.title} | SEG Career Support Blog`;
      
      // Get or create meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      const description = post.excerpt || post.content?.replace(/<[^>]*>/g, '').substring(0, 160) + '...';
      metaDesc.content = description;
      
      // Set Open Graph tags
      const setOrCreateMeta = (property, content, isProperty = true) => {
        const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`;
        let meta = document.querySelector(selector);
        if (!meta) {
          meta = document.createElement('meta');
          if (isProperty) {
            meta.setAttribute('property', property);
          } else {
            meta.name = property;
          }
          document.head.appendChild(meta);
        }
        meta.content = content || '';
      };
      
      const fullUrl = `${window.location.origin}/blog/${slug}`;
      
      // Open Graph tags
      setOrCreateMeta('og:type', 'article');
      setOrCreateMeta('og:url', fullUrl);
      setOrCreateMeta('og:title', post.title);
      setOrCreateMeta('og:description', description);
      setOrCreateMeta('og:image', post.featured_image);
      setOrCreateMeta('og:site_name', 'SEG Career Support');
      
      // Twitter tags
      setOrCreateMeta('twitter:card', 'summary_large_image', false);
      setOrCreateMeta('twitter:title', post.title, false);
      setOrCreateMeta('twitter:description', description, false);
      setOrCreateMeta('twitter:image', post.featured_image, false);
      
      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = fullUrl;
    }
    
    // Cleanup function to reset title
    return () => {
      document.title = 'SEG Career Support';
    };
  }, [post, slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-heading font-bold mb-4">Post Not Found</h1>
            <p className="text-muted-foreground mb-8">The blog post you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/blog">Back to Blog</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const fullUrl = `${window.location.origin}/blog/${slug}`;
  const breadcrumbData = [
    { name: 'Home', url: window.location.origin },
    { name: 'Blog', url: `${window.location.origin}/blog` },
    { name: post.title, url: fullUrl }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* JSON-LD Structured Data */}
      <JsonLd data={generateBlogPostSchema(post, fullUrl)} />
      <JsonLd data={generateBreadcrumbSchema(breadcrumbData)} />
      
      <Header />
      
      <main className="flex-1">
        <PageBanner 
          title={post.title} 
          breadcrumbs={[
            { label: 'Blog', path: '/blog' },
            { label: post.title }
          ]} 
        />

        <article className="py-16" data-testid="blog-post-article">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                {/* Featured Image */}
                {post.featured_image && (
                  <div className="rounded-2xl overflow-hidden mb-8">
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-auto"
                    />
                  </div>
                )}

                {/* Meta */}
                <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(post.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {post.author}
                  </span>
                  {post.categories?.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      {post.categories.map((cat, i) => (
                        <Link
                          key={cat}
                          to={`/blog?category=${cat}`}
                          className="hover:text-primary transition-colors"
                        >
                          {cat}{i < post.categories.length - 1 ? ', ' : ''}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div 
                  className="blog-content prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* Tags */}
                {post.tags?.length > 0 && (
                  <div className="mt-8 pt-8 border-t">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">Tags:</span>
                      {post.tags.map((tag) => (
                        <Badge key={tag} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Back to Blog */}
                <div className="mt-8">
                  <Button asChild variant="outline" data-testid="back-to-blog-btn">
                    <Link to="/blog">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-8">
                {/* Recent Posts */}
                <Card data-testid="sidebar-recent-posts">
                  <CardHeader>
                    <CardTitle>Recent Posts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-4">
                      {recentPosts.slice(0, 4).map((recentPost) => (
                        <li key={recentPost.id}>
                          <Link
                            to={`/blog/${recentPost.slug}`}
                            className="group flex gap-3"
                          >
                            {recentPost.featured_image && (
                              <img
                                src={recentPost.featured_image}
                                alt={recentPost.title}
                                className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                              />
                            )}
                            <div>
                              <h4 className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2">
                                {recentPost.title}
                              </h4>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDate(recentPost.created_at)}
                              </p>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Categories */}
                <Card data-testid="sidebar-categories">
                  <CardHeader>
                    <CardTitle>Categories</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {categories.map((category) => (
                        <li key={category.id}>
                          <Link
                            to={`/blog?category=${category.slug}`}
                            className="text-sm hover:text-primary transition-colors flex justify-between"
                          >
                            <span>{category.name}</span>
                            <span className="text-muted-foreground">({category.post_count})</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
